/**
 * DNS Records Section
 *
 * Extracted from domain detail page for use in the Configuration tab.
 * Shows current SPF/DMARC records, suggested changes, and SPF optimization wizard.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/lib/toast";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Copy,
  Sparkles,
  Lock,
} from "lucide-react";
import { api, Domain } from "@/lib/api";
import { SpfOptimizationWizard } from "@/components/spf/SpfOptimizationWizard";

export function DnsSection({ domain, onRefresh, tier }: { domain: Domain; onRefresh: () => void; tier: string }) {
  const navigate = useNavigate();
  const [spfData, setSpfData] = useState<{
    current_spf: string | null;
    suggested_spf: string;
    warnings: string[];
  } | null>(null);
  const [dmarcData, setDmarcData] = useState<{
    current_dmarc: string | null;
    current_policy: string | null;
    recommendation: {
      suggested_record: string;
      reasoning: string;
    };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [cooldownEnd, setCooldownEnd] = useState<Date | null>(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [showSpfWizard, setShowSpfWizard] = useState(false);

  // Check if user has AI features (Pro+)
  const hasAiFeatures = tier === "pro" || tier === "enterprise" || tier === "enterprise_plus";

  useEffect(() => {
    fetchDnsData();
  }, [domain.id]);

  // Cooldown timer
  useEffect(() => {
    if (!cooldownEnd) return;

    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((cooldownEnd.getTime() - Date.now()) / 1000));
      setCooldownSeconds(remaining);
      if (remaining === 0) {
        setCooldownEnd(null);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [cooldownEnd]);

  async function fetchDnsData() {
    try {
      setLoading(true);
      const [spf, dmarc] = await Promise.all([
        api.getSuggestedSpf(domain.id),
        api.getSuggestedDmarc(domain.id),
      ]);
      setSpfData(spf);
      setDmarcData(dmarc);
    } catch (error) {
      console.error("Failed to fetch DNS data:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleRefreshDns() {
    if (refreshing || cooldownSeconds > 0) return;

    try {
      setRefreshing(true);
      const result = await api.refreshDns(domain.id);

      // Update local state with new DNS data
      setSpfData(prev => prev ? { ...prev, current_spf: result.spf.raw } : null);
      setDmarcData(prev => prev ? { ...prev, current_dmarc: result.dmarc.raw } : null);

      // Set cooldown from server response
      if (result.next_refresh_available_at) {
        setCooldownEnd(new Date(result.next_refresh_available_at));
      }

      // Refetch domain data to update current_policy in parent component
      onRefresh();
    } catch (error: unknown) {
      // Handle rate limit error
      if (error && typeof error === 'object' && 'status' in error && (error as { status: number }).status === 429) {
        const errData = error as { data?: { retry_after?: number; reset_at?: string } };
        if (errData.data?.reset_at) {
          setCooldownEnd(new Date(errData.data.reset_at));
        } else if (errData.data?.retry_after) {
          setCooldownEnd(new Date(Date.now() + errData.data.retry_after * 1000));
        }
      }
      console.error("Failed to refresh DNS:", error);
      toast.error("Failed to refresh DNS records");
    } finally {
      setRefreshing(false);
    }
  }

  function copyToClipboard(text: string, label: string) {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  }

  function formatCooldown(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  const suggestedDmarcRecord = dmarcData?.recommendation.suggested_record ||
    `v=DMARC1; p=${domain.policy_goal || "none"}; rua=mailto:dmarc@verkh.io`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <>
    <div className="space-y-6">
      {/* Current DNS Records */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-800">
            Current DNS Records
          </h3>
          <button
            onClick={handleRefreshDns}
            disabled={refreshing || cooldownSeconds > 0}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              refreshing || cooldownSeconds > 0
                ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                : "bg-amber-50 text-amber-700 hover:bg-amber-100"
            }`}
            title={cooldownSeconds > 0 ? `Available in ${formatCooldown(cooldownSeconds)}` : "Refresh DNS records"}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing
              ? "Refreshing..."
              : cooldownSeconds > 0
                ? formatCooldown(cooldownSeconds)
                : "Refresh"}
          </button>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          These are the DNS records currently configured for your domain.
        </p>

        <div className="space-y-4">
          {/* DMARC Status */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-700">DMARC Record</span>
                {dmarcData?.current_dmarc ? (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                    <CheckCircle2 className="w-3 h-3" />
                    Configured
                  </span>
                ) : (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                    <XCircle className="w-3 h-3" />
                    Missing
                  </span>
                )}
              </div>
              <span className="text-sm text-slate-500 font-mono">
                _dmarc.{domain.domain_name}
              </span>
            </div>
            {dmarcData?.current_dmarc ? (
              <code className="block p-3 bg-white border border-slate-200 rounded-lg text-sm font-mono overflow-x-auto">
                {dmarcData.current_dmarc}
              </code>
            ) : (
              <p className="text-sm text-red-600">
                No DMARC record found. Add one below to start receiving reports.
              </p>
            )}
          </div>

          {/* SPF Status */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-700">SPF Record</span>
                {spfData?.current_spf ? (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                    <CheckCircle2 className="w-3 h-3" />
                    Configured
                  </span>
                ) : (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                    <AlertTriangle className="w-3 h-3" />
                    Not Found
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {hasAiFeatures ? (
                  <button
                    onClick={() => setShowSpfWizard(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-amber-500 text-slate-900 rounded-lg hover:bg-amber-400 transition-all shadow-sm"
                  >
                    <Sparkles className="w-4 h-4" />
                    AI Optimize
                  </button>
                ) : (
                  <button
                    onClick={() => navigate("/settings/billing")}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-slate-100 text-slate-500 rounded-lg hover:bg-slate-200 transition-colors"
                    title="Upgrade to Pro for AI-powered SPF optimization"
                  >
                    <Lock className="w-3.5 h-3.5" />
                    <Sparkles className="w-3.5 h-3.5" />
                    AI Optimize
                  </button>
                )}
                <span className="text-sm text-slate-500 font-mono">
                  {domain.domain_name}
                </span>
              </div>
            </div>
            {spfData?.current_spf ? (
              <code className="block p-3 bg-white border border-slate-200 rounded-lg text-sm font-mono overflow-x-auto">
                {spfData.current_spf}
              </code>
            ) : (
              <p className="text-sm text-amber-600">
                No SPF record found. SPF helps prevent email spoofing.
              </p>
            )}
          </div>

          {/* Verification Status */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-700">Domain Verification</span>
                {domain.verification_status === "verified" ? (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                    <CheckCircle2 className="w-3 h-3" />
                    Verified
                  </span>
                ) : (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                    <AlertTriangle className="w-3 h-3" />
                    Pending
                  </span>
                )}
              </div>
              <span className="text-sm text-slate-500 font-mono">
                _dmarc-verify.{domain.domain_name}
              </span>
            </div>
            {domain.verification_status === "verified" ? (
              <p className="text-sm text-emerald-700">
                Domain ownership has been verified. You can remove the verification TXT record if desired.
              </p>
            ) : (
              <p className="text-sm text-amber-600">
                Domain verification pending. Add the verification record below.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Required DNS Records to Add */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-2">
          DNS Records to Add
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          Add these TXT records to your DNS provider to complete setup.
        </p>

        {/* DMARC Record to Add */}
        {!dmarcData?.current_dmarc && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <span className="font-semibold text-red-700">DMARC Record Required</span>
            </div>
            <p className="text-sm text-red-700 mb-3">
              Add this TXT record to start receiving DMARC reports and protect your domain from spoofing.
            </p>
            <div className="mb-2">
              <span className="text-xs font-medium text-slate-500 uppercase">Record Name</span>
              <code className="block mt-1 p-2 bg-white border border-red-200 rounded text-sm font-mono">
                _dmarc.{domain.domain_name}
              </code>
            </div>
            <div className="mb-3">
              <span className="text-xs font-medium text-slate-500 uppercase">Record Value</span>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 p-2 bg-white border border-red-200 rounded text-sm font-mono overflow-x-auto">
                  {suggestedDmarcRecord}
                </code>
                <button
                  onClick={() => copyToClipboard(suggestedDmarcRecord, "dmarc")}
                  className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                  title="Copy to clipboard"
                >
                  {copied === "dmarc" ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-700" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Suggested DMARC Update */}
        {dmarcData?.current_dmarc && dmarcData.recommendation.suggested_record !== dmarcData.current_dmarc && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <RefreshCw className="w-5 h-5 text-blue-500" />
              <span className="font-semibold text-blue-700">DMARC Update Recommended</span>
            </div>
            <p className="text-sm text-blue-700 mb-3">
              {dmarcData.recommendation.reasoning}
            </p>
            <div className="mb-2">
              <span className="text-xs font-medium text-slate-500 uppercase">Record Name</span>
              <code className="block mt-1 p-2 bg-white border border-blue-200 rounded text-sm font-mono">
                _dmarc.{domain.domain_name}
              </code>
            </div>
            <div className="mb-3">
              <span className="text-xs font-medium text-slate-500 uppercase">Suggested Value</span>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 p-2 bg-white border border-blue-200 rounded text-sm font-mono overflow-x-auto">
                  {dmarcData.recommendation.suggested_record}
                </code>
                <button
                  onClick={() => copyToClipboard(dmarcData.recommendation.suggested_record, "dmarc-update")}
                  className="p-2 text-blue-500 hover:bg-blue-100 rounded-lg transition-colors"
                  title="Copy to clipboard"
                >
                  {copied === "dmarc-update" ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-700" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Verification TXT Record */}
        {domain.verification_status !== "verified" && domain.verification_token && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <span className="font-semibold text-amber-800">Verification Record Required</span>
            </div>
            <p className="text-sm text-amber-700 mb-3">
              Add this TXT record to verify domain ownership. This confirms you control this domain.
            </p>
            <div className="mb-2">
              <span className="text-xs font-medium text-slate-500 uppercase">Record Name</span>
              <code className="block mt-1 p-2 bg-white border border-amber-200 rounded text-sm font-mono">
                _dmarc-verify.{domain.domain_name}
              </code>
            </div>
            <div className="mb-3">
              <span className="text-xs font-medium text-slate-500 uppercase">Record Value</span>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 p-2 bg-white border border-amber-200 rounded text-sm font-mono overflow-x-auto">
                  {domain.verification_token}
                </code>
                <button
                  onClick={() => copyToClipboard(domain.verification_token!, "verify")}
                  className="p-2 text-amber-600 hover:bg-amber-100 rounded-lg transition-colors"
                  title="Copy to clipboard"
                >
                  {copied === "verify" ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-700" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* All Good Message - split by enforcement level */}
        {dmarcData?.current_dmarc &&
          dmarcData.recommendation.suggested_record === dmarcData.current_dmarc &&
          domain.verification_status === "verified" && (
          (() => {
            const policy = domain.current_policy || dmarcData.current_policy;
            const atEnforcement = policy === "quarantine" || policy === "reject";
            return atEnforcement ? (
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-700" />
                  <span className="font-semibold text-emerald-700">DNS Configuration Complete</span>
                </div>
                <p className="text-sm text-emerald-700 mt-1">
                  Your domain is properly configured with DMARC at p={policy} and verified. No action needed.
                </p>
              </div>
            ) : (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <span className="font-semibold text-amber-700">DMARC Record Published</span>
                </div>
                <p className="text-sm text-amber-700 mt-1">
                  Reporting is active at p=none. Review your sending sources, then advance to p=quarantine when ready.
                </p>
              </div>
            );
          })()
        )}
      </div>
    </div>

      {/* SPF Optimization Wizard Modal */}
      {showSpfWizard && (
        <SpfOptimizationWizard
          domainId={domain.id}
          domainName={domain.domain_name}
          currentSpf={spfData?.current_spf || null}
          onClose={() => setShowSpfWizard(false)}
          onSuccess={() => {
            fetchDnsData();
            onRefresh();
          }}
        />
      )}
    </>
  );
}
